import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarClock, FileSignature, FileText, Loader2, Receipt, Wallet } from 'lucide-react';
import { fmtEur, fmtDate } from '../paiementsCb/gedUtils';
import { openBlob } from '../comptabilite/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/espace-membre`;
const SECTIONS = [
  ['factures', 'Factures', FileText, 'text-sky-700'],
  ['recus', 'Reçus', Receipt, 'text-emerald-700'],
  ['echeanciers', 'Échéanciers', CalendarClock, 'text-violet-700'],
  ['mandats', 'Mandats SEPA signés', FileSignature, 'text-amber-700'],
];

const pathOf = (d) => (d.ged_id ? `/espace-membre/documents/${d.ged_id}/download`
  : d.facture_id ? `/factures-plans/${d.facture_id}/pdf` : d.kind && d.kind !== 'ged' ? `/espace-membre/echeanciers/${d.kind}/${encodeURIComponent(d.id)}/pdf` : null);

const Doc = ({ d, tid, headers }) => {
  const path = pathOf(d);
  return (
    <li className="flex flex-wrap items-center gap-2 py-2 border-t border-slate-100 text-sm" data-testid={tid}>
      <span className="font-medium text-slate-900">{d.titre}</span>
      {d.reference && <span className="font-mono text-xs text-slate-500">{d.reference}</span>}
      <span className="text-xs text-slate-500">{fmtDate(d.date)}</span>
      {d.statut && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{d.statut}</span>}
      {d.montant ? <span className="ml-auto tabular-nums font-semibold">{fmtEur(d.montant)}</span> : <span className="ml-auto" />}
      <button type="button" disabled={!path} onClick={() => openBlob(path, headers)} data-testid={`${tid}-open`}
        className="text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-40">{path ? 'Ouvrir' : 'Sans PDF'}</button>
    </li>
  );
};

const Echeancier = ({ e, tid, headers }) => (
  <li className="py-2 border-t border-slate-100 space-y-1" data-testid={tid}>
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="font-medium text-slate-900">{e.produit}</span>
      <span className="font-mono text-xs text-slate-500">{e.reference}</span>
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{e.mode}</span>
      <span className="ml-auto text-xs text-slate-600">Réglé {fmtEur(e.paye)} / {fmtEur(e.total)} · reste <strong>{fmtEur(e.reste)}</strong></span>
      <button type="button" onClick={() => openBlob(pathOf(e), headers)} data-testid={`${tid}-pdf`} className="text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50">PDF</button>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {e.echeances.map((x, i) => (
        <span key={i} className={`text-[11px] rounded-md border px-2 py-0.5 ${x.statut === 'paye' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : x.statut === 'echec' ? 'border-red-200 bg-red-50 text-red-700' : 'border-slate-200 text-slate-600'}`}>
          {i + 1}/{e.echeances.length} · {fmtDate(x.date)} · {fmtEur(x.montant)}
        </span>
      ))}
    </div>
  </li>
);

export const MesPiecesComptables = ({ getAuthHeader }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/pieces`, { headers: getAuthHeader() }).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const headers = getAuthHeader();
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="mes-pieces-comptables">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-[#0a3d62]"><Wallet className="h-5 w-5" /> Factures, reçus, échéanciers & mandats</h2>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : data.error ? <p className="text-sm text-red-600">Chargement impossible</p> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {SECTIONS.map(([k, label, Icon, cls]) => (
            <div key={k} className="rounded-xl border border-slate-100 bg-slate-50/50 p-3" data-testid={`pieces-section-${k}`}>
              <h3 className={`flex items-center gap-2 text-sm font-semibold ${cls}`}><Icon className="h-4 w-4" /> {label} <span className="text-slate-400 font-normal" data-testid={`pieces-count-${k}`}>({data[k].length})</span></h3>
              {data[k].length === 0 ? <p className="text-xs text-slate-500 pt-2" data-testid={`pieces-empty-${k}`}>Aucun document.</p> : (
                <ul>{data[k].map((d, i) => (k === 'echeanciers' && d.kind !== 'ged'
                  ? <Echeancier key={i} e={d} tid={`piece-${k}-${i}`} headers={headers} />
                  : <Doc key={i} d={d} tid={`piece-${k}-${i}`} headers={headers} />))}</ul>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
